import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { 
  AddOutlined, 
  CloseOutlined, 
  PersonOutlined,
  ExpandMoreOutlined,
  ExpandLessOutlined 
} from '@mui/icons-material';
import LockOutlined from '@mui/icons-material/LockOutlined';
import ConfirmBar from '../../atoms/ConfirmBar/ConfirmBar';
import authService from '../../../services/authService';
import './Supervisionmodal.css';

// ── Shared comparator: active first, then A→Z by name ─────────────
const byStatusThenName = (a, b) => {
  if (a.active !== b.active) return a.active ? -1 : 1;
  const nameA = `${a.name ?? ''} ${a.lastname ?? ''}`.toLowerCase();
  const nameB = `${b.name ?? ''} ${b.lastname ?? ''}`.toLowerCase();
  return nameA.localeCompare(nameB);
};

const SupervisionModal = ({ admin, allUsers = [], onClose, onAdd, onRemove }) => {
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'add'|'remove'|'add-multiple', user, users }
  const [showAddList, setShowAddList] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [selectedToAdd, setSelectedToAdd] = useState(new Set());

  // ── NOUVEAU : liste chargée via API, plus de filtrage sur allUsers ──
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [fetchedCandidates, setFetchedCandidates] = useState(null); // null = pas encore chargé

  const currentUser  = useSelector((state) => state.auth.user);
  const isSuperAdmin = currentUser?.role === 'superadmin';

  const buildTree = (rootId) => {
    const directChildren = allUsers.filter((u) => {
      if (u.id === rootId) return false;
      const ids = u.supervisor_ids || [];
      return ids.map(String).includes(String(rootId));
    });

    return directChildren
      .sort(byStatusThenName)
      .map((child) => ({
        ...child,
        children: buildTree(child.id),
      }));
  };

  const supervisedTree = buildTree(admin.id);

  const countAllSupervised = (nodes) =>
    nodes.reduce((count, node) => count + 1 + countAllSupervised(node.children || []), 0);

  const totalSupervised = countAllSupervised(supervisedTree);

  // ── Liste disponible : vient désormais de l'API, plus de filtre local ──
  const availableAdmins = (fetchedCandidates ?? []).slice().sort(byStatusThenName);

  const toggleNode = (nodeId) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      next.has(nodeId) ? next.delete(nodeId) : next.add(nodeId);
      return next;
    });
  };

  const toggleSelectAdmin = (id) => {
    setSelectedToAdd((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectedAdminObjects = availableAdmins.filter((u) => selectedToAdd.has(u.id));

  // ── Récupère admins + users disponibles à superviser, au clic ──────
  const handleToggleAddList = async () => {
    const next = !showAddList;
    setShowAddList(next);

    if (next && fetchedCandidates === null) {
      setLoadingAvailable(true);
      try {
        const data = await authService.getAvailableSupervisees(admin.id);
        setFetchedCandidates(data);
      } catch (e) {
        setFetchedCandidates([]);
      } finally {
        setLoadingAvailable(false);
      }
    }
  };

  const handleAddSelectedClick = () => {
    if (selectedAdminObjects.length === 0) return;
    setShowAddList(false);
    setConfirmAction({ type: 'add-multiple', users: selectedAdminObjects });
  };

  const handleRemoveClick = (u) => {
    setShowAddList(false);
    setConfirmAction({ type: 'remove', user: u });
  };

  const handleConfirm = () => {
    if (confirmAction.type === 'add-multiple') {
      confirmAction.users.forEach((u) => onAdd(u.id));
      setSelectedToAdd(new Set());
      // ── invalide le cache pour refléter les nouveaux ajouts au prochain clic ──
      setFetchedCandidates(null);
    }
    if (confirmAction.type === 'remove') onRemove(confirmAction.user.id);
    setConfirmAction(null);
  };

  const handleCancel = () => setConfirmAction(null);

  const avatarColor = (role) => {
    if (role === 'admin')      return '#172631';
    if (role === 'superadmin') return '#7c3aed';
    return '#64748b';
  };

  const TreeNode = ({ node, depth = 0 }) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded  = expandedNodes.has(node.id);

    return (
      <div className="tree-node-wrapper">
        <div className={`tree-node depth-${depth}`}>
          {depth > 0 && <div className="tree-connector" />}
          <div className="tree-content">
            {hasChildren && (
              <button className="tree-toggle" onClick={() => toggleNode(node.id)}>
                {isExpanded ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
                <span className="tree-child-count">{node.children.length}</span>
              </button>
            )}
            <div className="tree-avatar" style={{ background: avatarColor(node.role) }}>
              {node.name?.[0]?.toUpperCase()}{node.lastname?.[0]?.toUpperCase()}
            </div>
            <div className="tree-info">
              <span className="tree-name">
                {node.name} {node.lastname}
              </span>
              <span className="tree-email">{node.email}</span>
            </div>
            <span
              className={`tree-status-dot ${node.active ? 'active' : 'inactive'}`}
              title={node.active ? 'Actif' : 'Inactif'}
            />
            {node.role === 'admin' ? (
              <button
                className="tree-remove-btn"
                onClick={() => handleRemoveClick(node)}
                title="Retirer de la supervision"
              >
                <CloseOutlined fontSize="small" />
              </button>
            ) : (
              <LockOutlined
                className="tree-locked-icon"
                titleAccess="Un utilisateur doit toujours avoir un admin superviseur"
                fontSize="small"
              />
            )}
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div className="tree-children">
            {node.children.map((child) => (
              <TreeNode key={child.id} node={child} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="sv-overlay">
      <div className="sv-modal" onClick={(e) => e.stopPropagation()}>

        <div className="sv-header">
          <div className="sv-header-left">
            <div className="sv-admin-avatar">
              {admin.name?.[0]?.toUpperCase()}{admin.lastname?.[0]?.toUpperCase()}
            </div>
            <div>
              <h3 className="sv-title">Supervision</h3>
              <p className="sv-subtitle">
                {admin.name} {admin.lastname}
                <span className="sv-role-badge">{admin.role}</span>
              </p>
              <p className="sv-header-desc">
                Admins et Utilisateurs supervisés <strong>par</strong> {admin.name} {admin.lastname}
              </p>
            </div>
          </div>
          <button className="sv-close" onClick={onClose} title="Fermer">
            <CloseOutlined />
          </button>
        </div>

        <div className="sv-section">
          <div className="sv-section-header">
            <span className="sv-section-title">
              Arborescence de supervision
            </span>

            {isSuperAdmin && (
              <button
                className="sv-add-btn"
                onClick={handleToggleAddList}
                disabled={loadingAvailable}
                title="Superviser un admin ou un utilisateur"
              >
                <AddOutlined fontSize="small" />
                {loadingAvailable ? 'Chargement…' : 'Ajouter un admin/utilisateur à superviser'}
              </button>
            )}
          </div>

          {isSuperAdmin && showAddList && (
            <div className="sv-add-dropdown">
              {loadingAvailable ? (
                <div className="sv-add-loading">
                  <span className="sv-add-spinner" />
                  Chargement des comptes disponibles…
                </div>
              ) : availableAdmins.length === 0 ? (
                <p className="sv-add-empty-text">Aucun admin ou utilisateur disponible</p>
              ) : (
                <>
                  <p className="sv-add-label">Sélectionner un ou plusieurs admins/utilisateurs à superviser :</p>
                  <ul className="sv-add-list">
                    {availableAdmins.map((u) => {
                      const checked = selectedToAdd.has(u.id);
                      return (
                        <li
                          key={u.id}
                          className={`sv-add-item${checked ? ' sv-add-item--checked' : ''}`}
                          onClick={() => toggleSelectAdmin(u.id)}
                        >
                          <input
                            type="checkbox"
                            className="sv-add-checkbox"
                            checked={checked}
                            onChange={() => toggleSelectAdmin(u.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="sv-user-avatar sm" style={{ background: avatarColor(u.role) }}>
                            {u.name?.[0]?.toUpperCase()}{u.lastname?.[0]?.toUpperCase()}
                          </div>
                          <div className="sv-user-info">
                            <span className="sv-user-name">
                              {u.name} {u.lastname}
                              <span className={`tree-role-tag ${u.role}`}>{u.role}</span>
                            </span>
                            <span className="sv-user-email">{u.email}</span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>

                  <div className="sv-add-footer">
                    <span className="sv-add-selected-count">
                      {selectedToAdd.size > 0
                        ? `${selectedToAdd.size} sélectionné(s)`
                        : 'Aucune sélection'}
                    </span>
                    <button
                      className="sv-add-confirm-btn"
                      onClick={handleAddSelectedClick}
                      disabled={selectedToAdd.size === 0}
                    >
                      Ajouter {selectedToAdd.size > 0 ? `(${selectedToAdd.size})` : ''}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {supervisedTree.length === 0 ? (
            <div className="sv-empty">
              <PersonOutlined />
              <p>Aucun supervisé pour le moment</p>
            </div>
          ) : (
            <div className="tree-container">
              {supervisedTree.map((node) => (
                <TreeNode key={node.id} node={node} depth={0} />
              ))}
            </div>
          )}
        </div>

        {confirmAction && (
          <ConfirmBar
            message={
              confirmAction.type === 'add-multiple'
                ? `Ajouter ${confirmAction.users.length} admin(s) à la supervision`
                : confirmAction.type === 'remove'
                ? 'Retirer la supervision de'
                : 'Ajouter à la supervision'
            }
            highlight={
              confirmAction.type === 'add-multiple'
                ? confirmAction.users.map((u) => `${u.name} ${u.lastname}`).join(', ')
                : `${confirmAction.user.name} ${confirmAction.user.lastname}`
            }
            onConfirm={handleConfirm}
            onCancel={handleCancel}
          />
        )}

      </div>
    </div>
  );
};

export default SupervisionModal;